import { InputAdornment, Stack, Typography } from '@mui/material';
import type { InputAdornmentProps } from '@mui/material/InputAdornment';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { _, getActiveLanguage } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import { getValidPlantingDates } from '/@/store/private-data/recommendations/selectors';
import type { Field } from '/@/types/field';
import { createPlantingDateValidator } from '/@/utils/date';

interface PlantingDateInputProps {
    value: string | undefined;
    onChange: (date: string) => void;
    field: Field;
    crop: string;
    error?: string;
    endAdornment?: ReactNode;
    showValidDateHelperText?: boolean;
    anchorDate?: string;
    windowDays?: number;
    /** Skips the internal label — for callers rendering their own (e.g. an inline row layout). */
    hideLabel?: boolean;
    disabled?: boolean;
    /** Drops the "fechas recomendadas" hint paragraph — for callers whose layout has no room
     *  for it (e.g. a narrow inline row). */
    hideHelperText?: boolean;
}

interface ExtraInputAdornmentProps extends InputAdornmentProps {
    extra?: ReactNode;
}

// Defined at module level so the slot reference is stable and the picker never remounts.
// slotProps.inputAdornment passes `extra` reactively alongside the calendar button.
const ExtraInputAdornment = ({ extra, children, ...props }: ExtraInputAdornmentProps) => (
    <InputAdornment {...props}>
        {extra}
        {children}
    </InputAdornment>
);

/** Matches full formatDate default but with 2-digit year for compact helper text. */
const formatShortPlantingDate = (isoDate: string) => {
    const language = getActiveLanguage();
    return new Date(isoDate).toLocaleDateString(language, {
        timeZone: 'UTC',
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
    });
};

/** Merge ranges that overlap or are contiguous (next start <= previous end + 1 day). */
const mergePlantingDateRanges = (validDates: string[][]): [string, string][] => {
    const parsed = validDates
        .filter(([from, to]) => from && to)
        .map(([from, to]) => ({
            start: dayjs(from).startOf('day'),
            end: dayjs(to).startOf('day'),
        }))
        .sort((a, b) => (a.start.isBefore(b.start) ? -1 : a.start.isAfter(b.start) ? 1 : 0));

    const merged: { start: dayjs.Dayjs; end: dayjs.Dayjs }[] = [];
    for (const r of parsed) {
        const last = merged[merged.length - 1];
        if (!last || r.start.isAfter(last.end.add(1, 'day'))) {
            merged.push({ start: r.start, end: r.end });
        } else if (r.end.isAfter(last.end)) {
            last.end = r.end;
        }
    }

    return merged.map((m) => [m.start.format('YYYY-MM-DD'), m.end.format('YYYY-MM-DD')]);
};

const formatDateRanges = (validDates: string[][]) => {
    const ranges = mergePlantingDateRanges(validDates).map(
        ([from, to]) => `${formatShortPlantingDate(from)} al ${formatShortPlantingDate(to)}`
    );
    if (ranges.length <= 2) {
        return ranges.join(' y ');
    }
    return `${ranges.slice(0, -1).join(', ')} y ${ranges[ranges.length - 1]}`;
};

const PlantingDateInput = ({
    value,
    onChange,
    field,
    crop,
    error,
    endAdornment,
    showValidDateHelperText = true,
    anchorDate,
    windowDays = 14,
    hideLabel,
    hideHelperText,
    disabled,
}: PlantingDateInputProps) => {
    const validDates = useMemo(() => {
        if (!field.node.zones?.length) return [];
        return getValidPlantingDates({ crop }, field);
    }, [field, crop]);

    const validDatesString = useMemo(() => {
        if (!validDates?.length) return '';
        return formatDateRanges(validDates);
    }, [validDates]);

    const shouldDisableDate = useMemo(() => {
        const baseValidator = crop === 'corn' ? createPlantingDateValidator(validDates, [-1, 0, 1]) : () => false;
        if (!anchorDate) return baseValidator;
        // Keep every hybrid within `windowDays` of the first hybrid's planting date.
        const min = dayjs(anchorDate).startOf('day').subtract(windowDays, 'day');
        const max = dayjs(anchorDate).startOf('day').add(windowDays, 'day');
        return (date: dayjs.Dayjs) => {
            if (baseValidator(date)) return true;
            const day = date.startOf('day');
            return day.isBefore(min) || day.isAfter(max);
        };
    }, [validDates, crop, anchorDate, windowDays]);

    return (
        <Stack spacing={0.5} sx={{ width: '100%' }}>
            {!hideLabel && (
                <Stack spacing={0.5}>
                    <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                        {_('Fecha de siembra')}
                    </Typography>
                </Stack>
            )}
            <MuiDatePicker
                disabled={disabled}
                value={value ? dayjs(value) : undefined}
                onChange={(e) => onChange(e ? e.format('YYYY-MM-DD') : '')}
                fullWidth
                slots={{ inputAdornment: ExtraInputAdornment }}
                slotProps={{
                    textField: {
                        error: !!error,
                        helperText: error,
                        inputProps: {
                            'data-testid': 'field-monitoring-creation-dialog-planting-date-picker',
                        },
                        placeholder: _('Seleccione una fecha'),
                    },
                    inputAdornment: { extra: endAdornment } as ExtraInputAdornmentProps,
                }}
                shouldDisableDate={shouldDisableDate}
            />
            {validDatesString && showValidDateHelperText && !hideHelperText && (
                <Typography
                    variant='body2'
                    color='primary.mui.inactive'
                    sx={{
                        mx: '14px !important',
                        mt: '3px !important',
                    }}
                >
                    {_('Las fechas de siembra recomendadas para tu área son del {{dates}}.', {
                        dates: validDatesString,
                    })}
                </Typography>
            )}
        </Stack>
    );
};

export default PlantingDateInput;
